import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { createMask } from '@ngneat/input-mask';

import { AuthService } from 'src/app/services/auth/auth.service';
import { TaxFormBLL } from 'src/app/services/tax-form/tax-form.bll';
import {
  NAME_TYPES,
  US_STATES,
  irs_recipient_profile,
  set_irs_recipient_profile,
} from 'src/app/services/tax-form/tax-form.model';

/**
 * The eleven fields that make up a 1099-NEC recipient block, defined once and
 * used in two places:
 *
 *   onboarding — the blocking step a referee sees on first sign-in. Adds the
 *                certification checkbox and the typed signature, because this
 *                is the save that declares the taxpayer ID is correct.
 *   edit       — the same fields inside Settings > 1099. Changing an address is
 *                not a new declaration, so it does not re-prompt for one; the
 *                API preserves the signature already on file.
 */
@Component({
  selector: 'app-tax-onboarding',
  templateUrl: './tax-onboarding.component.html',
  styleUrls: ['./tax-onboarding.component.scss'],
})
export class TaxOnboardingComponent implements OnInit, OnDestroy {
  @Input() mode: 'onboarding' | 'edit' = 'onboarding';

  //#region Loaders

  isLoading$: Observable<boolean>;
  isSaving$: Observable<boolean>;

  //#endregion Loaders

  form: FormGroup;

  private subs: Subscription = new Subscription();

  /** Last four digits of the TIN already on file, if any. */
  tin_last_four: string;
  certified_at: string;

  nameTypes = NAME_TYPES;
  stateOptions = US_STATES;

  ssnInputMask = createMask<string>({
    mask: '999-99-9999',
    placeholder: 'xxx-xx-xxxx',
    positionCaretOnClick: 'select',
  });

  einInputMask = createMask<string>({
    mask: '99-9999999',
    placeholder: 'xx-xxxxxxx',
    positionCaretOnClick: 'select',
  });

  zipInputMask = createMask<string>({
    mask: '99999[-9999]',
    placeholder: 'xxxxx',
    positionCaretOnClick: 'select',
  });

  StateAlertOptions = {
    header: 'State / Province',
    subHeader: 'Select one',
  };

  constructor(
    private fb: FormBuilder,
    private bll: TaxFormBLL,
    private auth: AuthService
  ) {}

  ngOnInit() {
    this.initForm();
    this.initObservables();
    this.initDataRetrieval();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  //#region Init

  private initForm() {
    this.form = this.fb.group({
      // Taxpayer identification
      tin_type: ['ssn', Validators.required],
      tin: ['', [Validators.required, tinFormatValidator]],
      recipient_type: ['individual', Validators.required],
      legal_first_name: ['', [Validators.required, Validators.maxLength(80)]],
      legal_middle_name: ['', Validators.maxLength(80)],
      legal_last_name: ['', [Validators.required, Validators.maxLength(80)]],

      // Mailing address
      street_address: ['', [Validators.required, Validators.maxLength(120)]],
      apt_suite: ['', Validators.maxLength(60)],
      city: ['', [Validators.required, Validators.maxLength(60)]],
      state: ['', Validators.required],
      postal_code: ['', Validators.required],

      // Certification — onboarding only, cleared below when editing
      certifies_taxpayer_info: [false, Validators.requiredTrue],
      certification_name: ['', [Validators.required, Validators.maxLength(80)]],
    });

    if (this.mode === 'edit') {
      // An edit carries no declaration. Clearing the validators rather than
      // omitting the controls keeps one form definition for both modes.
      this.certifies_taxpayer_info.clearValidators();
      this.certification_name.clearValidators();
      this.certifies_taxpayer_info.updateValueAndValidity({ emitEvent: false });
      this.certification_name.updateValueAndValidity({ emitEvent: false });
    }
  }

  private initObservables() {
    this.isLoading$ = this.bll.isLoading$;
    this.isSaving$ = this.bll.isSaving$;
  }

  private initDataRetrieval() {
    // Seed the name from the account so a referee is not retyping what the
    // platform already knows. Overwritten by the stored profile if there is one.
    this.auth.auth$.pipe(take(1)).subscribe((user) => {
      const parts = (user?.name ?? '').trim().split(/\s+/).filter(Boolean);

      if (parts.length) {
        this.form.patchValue(
          {
            legal_first_name: parts[0],
            legal_last_name: parts.length > 1 ? parts[parts.length - 1] : '',
            certification_name: user?.name ?? '',
          },
          { emitEvent: false }
        );
      }
    });

    this.subs.add(
      this.bll.profile$.subscribe((profile) => {
        if (profile) {
          this.patchForm(profile);
        }
      })
    );

    this.bll.getRecipientProfile();
  }

  //#endregion Init

  //#region Form helpers

  get tin_type() { return this.form.get('tin_type'); }
  get tin() { return this.form.get('tin'); }
  get recipient_type() { return this.form.get('recipient_type'); }
  get legal_first_name() { return this.form.get('legal_first_name'); }
  get legal_middle_name() { return this.form.get('legal_middle_name'); }
  get legal_last_name() { return this.form.get('legal_last_name'); }
  get street_address() { return this.form.get('street_address'); }
  get apt_suite() { return this.form.get('apt_suite'); }
  get city() { return this.form.get('city'); }
  get state() { return this.form.get('state'); }
  get postal_code() { return this.form.get('postal_code'); }
  get certifies_taxpayer_info() { return this.form.get('certifies_taxpayer_info'); }
  get certification_name() { return this.form.get('certification_name'); }

  get has_tin_on_file(): boolean {
    return !!this.tin_last_four;
  }

  get tinLabel(): string {
    if (this.tin_type.value === 'ein') {
      return 'Employer Identification Number (EIN)';
    }

    return this.tin_type.value === 'itin'
      ? 'Individual Taxpayer Identification Number (ITIN)'
      : 'Social Security Number (SSN)';
  }

  /** EIN is xx-xxxxxxx; SSN and ITIN share xxx-xx-xxxx. */
  get tinMask() {
    return this.tin_type.value === 'ein' ? this.einInputMask : this.ssnInputMask;
  }

  changeTinType() {
    // The mask changes with the type, so a half-typed number under the old mask
    // would be re-interpreted rather than re-entered.
    this.tin.setValue('', { emitEvent: false });
  }

  //#endregion Form helpers

  //#region Actions

  private patchForm(profile: irs_recipient_profile) {
    this.tin_last_four = profile.tin_last_four;
    this.certified_at = profile.certified_at;

    this.form.patchValue(
      {
        tin_type: profile.tin_type ?? this.tin_type.value,
        recipient_type: profile.recipient_type ?? 'individual',
        legal_first_name: profile.legal_first_name ?? this.legal_first_name.value,
        legal_middle_name: profile.legal_middle_name ?? '',
        legal_last_name: profile.legal_last_name ?? this.legal_last_name.value,
        street_address: profile.street_address ?? '',
        apt_suite: profile.apt_suite ?? '',
        city: profile.city ?? '',
        state: profile.state ?? '',
        postal_code: profile.postal_code ?? '',
      },
      { emitEvent: false }
    );

    // With a TIN already stored, re-entering it is optional — the API keeps the
    // one on file. The format check still applies to anything typed.
    this.tin.setValidators([tinFormatValidator]);
    this.tin.updateValueAndValidity({ emitEvent: false });
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const set_profile: set_irs_recipient_profile = {
      recipient_type: this.recipient_type.value,
      legal_first_name: this.legal_first_name.value?.trim(),
      legal_middle_name: this.legal_middle_name.value?.trim(),
      legal_last_name: this.legal_last_name.value?.trim(),
      tin_type: this.tin_type.value,
      street_address: this.street_address.value?.trim(),
      apt_suite: this.apt_suite.value?.trim(),
      city: this.city.value?.trim(),
      state: this.state.value,
      postal_code: this.postal_code.value?.trim(),
    };

    // The declaration goes with the first save only. Sending it on an edit
    // would restate a certification the user was never shown.
    if (this.mode === 'onboarding') {
      set_profile.certifies_tin_correct = this.certifies_taxpayer_info.value;
      set_profile.certifies_us_person = this.certifies_taxpayer_info.value;
      set_profile.certification_name = this.certification_name.value?.trim();
    }

    // Only send the TIN when the user actually entered one, and strip the mask
    // so the API always receives digits.
    const tin_digits = (this.tin.value ?? '').replace(/\D/g, '');
    if (tin_digits) {
      set_profile.tin = tin_digits;
    }

    this.bll.setRecipientProfile(set_profile);

    // Clear the plain TIN out of the form as soon as it has been submitted.
    this.tin.setValue('', { emitEvent: false });
  }

  /**
   * The onboarding step cannot be dismissed, so signing out is the only way to
   * reach a different account from it. Without this a user who opened the wrong
   * account is stuck with no exit at all.
   */
  logout() {
    this.bll.resetTaxProfileCheck();
    this.auth.logout();
  }

  //#endregion Actions
}

/**
 * Nine digits once the mask separators are stripped. Deliberately only a shape
 * check — whether the number belongs to the name is something only IRS TIN
 * Matching can answer, and the API repeats this check anyway.
 */
export function tinFormatValidator(
  control: AbstractControl
): ValidationErrors | null {
  const digits = (control.value ?? '').replace(/\D/g, '');

  if (!digits) {
    return null;
  }

  return digits.length === 9 ? null : { tin_format: true };
}
